import React from 'react';
import { Box, Container, Paper, Typography } from '@mui/material';

const Section = ({ title, children }) => (
  <Box sx={{ mb: 3 }}>
    <Typography variant="h5" sx={{ mb: 1.25, fontWeight: 700 }}>
      {title}
    </Typography>
    {children}
  </Box>
);

const PrivacyPolicy = () => {
  return (
    <Container maxWidth="md" sx={{ py: { xs: 3, md: 5 } }}>
      <Paper elevation={2} sx={{ p: { xs: 2.5, md: 5 }, borderRadius: 2 }}>
        <Typography variant="h3" sx={{ mb: 0.5, fontWeight: 800 }}>
          Privacy Policy
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Effective Date: 19/04/2026
        </Typography>

        <Typography sx={{ mb: 3 }}>
          AdaptPrepare (&quot;we&quot;, &quot;our&quot;, &quot;us&quot;) is a practice-focused educational platform
          designed for students, parents, and teachers. We are committed to protecting
          children&apos;s privacy and handling data responsibly.
        </Typography>

        <Section title="1. Information We Collect">
          <Typography sx={{ fontWeight: 600, mb: 1 }}>a) Account Information</Typography>
          <Typography component="ul" sx={{ pl: 3, mb: 2 }}>
            <li>Parent / Teacher name</li>
            <li>Email address</li>
            <li>Role (Student / Parent / Teacher)</li>
          </Typography>

          <Typography sx={{ fontWeight: 600, mb: 1 }}>
            b) Student Information (Minimal & Purpose-Driven)
          </Typography>
          <Typography component="ul" sx={{ pl: 3, mb: 1 }}>
            <li>Student name or nickname</li>
            <li>Class / grade</li>
            <li>Practice responses</li>
            <li>Scores and time taken per question</li>
          </Typography>
          <Typography sx={{ mb: 2 }}>
            We do not collect sensitive personal information such as address, phone number,
            or government IDs for students.
          </Typography>

          <Typography sx={{ fontWeight: 600, mb: 1 }}>c) Usage & Practice Data</Typography>
          <Typography component="ul" sx={{ pl: 3, mb: 1 }}>
            <li>Practice attempts</li>
            <li>Question difficulty levels</li>
            <li>Accuracy and response time</li>
            <li>Improvement trends</li>
          </Typography>
          <Typography sx={{ mb: 1 }}>This data is used only to:</Typography>
          <Typography component="ul" sx={{ pl: 3, mb: 2 }}>
            <li>Adapt question difficulty</li>
            <li>Generate learning feedback</li>
            <li>Show progress to parents and teachers</li>
          </Typography>

          <Typography sx={{ fontWeight: 600, mb: 1 }}>d) AI features</Typography>
          <Typography component="ul" sx={{ pl: 3 }}>
            <li>
              For AI-related features (such as explanations, feedback, or similar), we send only the
              question and the responses needed for that interaction — nothing that identifies you as a
              user.
            </li>
            <li>
              User-related personal data is not used as input to AI; only question content and
              associated responses are used to power these features.
            </li>
          </Typography>
        </Section>

        <Section title="2. How We Use the Data">
          <Typography sx={{ mb: 1 }}>We use collected data to:</Typography>
          <Typography component="ul" sx={{ pl: 3, mb: 1 }}>
            <li>Enable personalised practice</li>
            <li>Adapt question difficulty</li>
            <li>Provide feedback on incorrect answers</li>
            <li>Show progress summaries</li>
            <li>Improve platform quality</li>
          </Typography>
          <Typography>
            <strong>We do NOT use student data for advertising or marketing.</strong>
          </Typography>
        </Section>

        <Section title="3. Data Sharing">
          <Typography sx={{ mb: 1 }}>We do not sell or rent personal data.</Typography>
          <Typography sx={{ mb: 1 }}>Data may be shared only:</Typography>
          <Typography component="ul" sx={{ pl: 3 }}>
            <li>With parents/teachers linked to the student</li>
          </Typography>
        </Section>

        <Section title="4. Children&apos;s Privacy">
          <Typography component="ul" sx={{ pl: 3 }}>
            <li>Student accounts are created and managed by parents or teachers.</li>
            <li>We follow a data minimisation approach - collecting only what is necessary for practice.</li>
            <li>Parents can request access, correction, or deletion of student data at any time.</li>
          </Typography>
        </Section>

        <Section title="5. Data Security">
          <Typography sx={{ mb: 1 }}>We use:</Typography>
          <Typography component="ul" sx={{ pl: 3, mb: 1 }}>
            <li>Secure servers</li>
            <li>Encrypted connections</li>
            <li>Restricted access controls</li>
          </Typography>
          <Typography>
            While no system is 100% secure, we take reasonable and industry-standard measures
            to protect data.
          </Typography>
        </Section>

        <Section title="6. Data Retention">
          <Typography component="ul" sx={{ pl: 3 }}>
            <li>Practice data is retained only while the account is active.</li>
            <li>Parents/teachers may request deletion of data at any time.</li>
            <li>Deleted data is permanently removed within a reasonable timeframe.</li>
          </Typography>
        </Section>

        <Section title="7. Your Rights">
          <Typography sx={{ mb: 1 }}>Parents and teachers have the right to:</Typography>
          <Typography component="ul" sx={{ pl: 3 }}>
            <li>View student data</li>
            <li>Correct inaccuracies</li>
            <li>Request deletion</li>
            <li>Withdraw consent</li>
          </Typography>
        </Section>

        <Section title="8. Updates to This Policy">
          <Typography component="ul" sx={{ pl: 3 }}>
            <li>We may update this policy from time to time.</li>
            <li>Significant changes will be communicated clearly.</li>
          </Typography>
        </Section>

        <Box sx={{ p: 2, borderLeft: '4px solid #00BCD4', bgcolor: 'background.default', borderRadius: 1 }}>
          <Typography variant="h6" sx={{ mb: 1, fontWeight: 700 }}>
            Contact Us
          </Typography>
          <Typography sx={{ mb: 0.5 }}>
            For any privacy-related questions or requests, please contact us at:
          </Typography>
          <Typography sx={{ fontWeight: 700 }}>support@adaptprepare.ai</Typography>
        </Box>
      </Paper>
    </Container>
  );
};

export default PrivacyPolicy;
